<template>
  <div></div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, shallowRef } from 'vue'
import * as THREE from 'three'

// 导入类型定义
import type { RadarTarget } from './types'

// Props 接口定义
interface RadarPointsProps {
  points: RadarTarget[]
  size: number
  color: string
  emissive: string
}

const props = withDefaults(defineProps<RadarPointsProps>(), {
  points: () => [],
  size: 0.06,
  color: '#00ffff',
  emissive: '#0ff',
})

// 暴露给父组件的引用，用于添加到场景中
const groupRef = shallowRef(new THREE.Group())

// 颜色映射
const getPointColor = (type: number): string => {
  switch (type) {
    case 1:
      return 'red'
    case 2:
      return 'green'
    case 3:
      return 'orange'
    default:
      return props.color
  }
}

// 更新点
const updatePoints = () => {
  const { points, size } = props

  // 清空现有网格
  while (groupRef.value.children.length > 0) {
    const mesh = groupRef.value.children[0]
    groupRef.value.remove(mesh)
    // 释放几何体和材质资源
    mesh.geometry.dispose()
    mesh.material.dispose()
  }

  // 创建新的点
  points.forEach((p) => {
    const x = p[0] / 100
    const y = p[1] / 100
    const z = p[2] / 100
    const type = p[3]

    const geometry = new THREE.SphereGeometry(size, 16, 16)
    const material = new THREE.MeshStandardMaterial({
      color: getPointColor(type),
      emissive: props.emissive,
    })

    const mesh = new THREE.Mesh(geometry, material)
    mesh.position.set(x, y, z)
    groupRef.value.add(mesh)
  })
}

// 监听points变化
watch(
  () => props.points,
  () => {
    updatePoints()
  },
  { deep: true }
)

// 监听size变化
watch(
  () => props.size,
  () => {
    updatePoints()
  }
)

// 监听color变化
watch(
  () => props.color,
  () => {
    updatePoints()
  }
)

// 初始化
onMounted(() => {
  updatePoints()
})

// 清理
onUnmounted(() => {
  // 清空所有网格并释放资源
  while (groupRef.value.children.length > 0) {
    const mesh = groupRef.value.children[0]
    groupRef.value.remove(mesh)
    mesh.geometry.dispose()
    mesh.material.dispose()
  }
})

// 暴露group给父组件
defineExpose({
  group: groupRef,
})
</script>

<style scoped lang="less"></style>
